import { Logo } from '../components';
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { authClient } from '../api';

export function AuthPage({ mode }: { mode: 'login' | 'signup' | 'forgot' }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Where to go after signing in: an invitation link or another protected page the user came from.
  const from = (useLocation().state as { from?: string } | null)?.from;
  const returnTo = from && from.startsWith('/') && !from.startsWith('//') ? from : '/';
  const ref = params.get('ref');
  if (ref) localStorage.setItem('tallypg_ref', ref);
  const invited = ref ?? localStorage.getItem('tallypg_ref');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signup') {
        const r = await authClient.signUp.email({ email, password, name: name || email.split('@')[0]! });
        if (r.error) throw new Error(r.error.message);
        setInfo('Account created. If email verification is enabled, check your inbox.');
        if (returnTo !== '/') localStorage.setItem('tallypg_return_to', returnTo);
        nav(returnTo);
      } else if (mode === 'login') {
        const r = await authClient.signIn.email({ email, password });
        if (r.error) throw new Error(r.error.message);
        nav(returnTo);
      } else {
        const r = await authClient.requestPasswordReset({ email, redirectTo: '/reset-password' });
        if (r.error) throw new Error(r.error.message);
        setInfo('If that address has an account, a reset link is on its way.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="brand" style={{ marginBottom: 20 }}>
        <Logo />
        <span>
          TallyPG
          <small>Normal PostgreSQL. Always available. Pay only for what you use.</small>
        </span>
      </div>
      <div className="card">
        <h1>
          {mode === 'signup' ? 'Create your account' : mode === 'login' ? 'Sign in' : 'Reset your password'}
        </h1>
        {mode === 'signup' && invited && (
          <div className="banner">Invited by a friend: you both get $5 of credit once you sign up.</div>
        )}
        {mode === 'signup' && (
          <p className="muted">
            No credit card required. New accounts start with $5.00 of credit. You will be asked to accept the{' '}
            <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link> before using the
            dashboard.
          </p>
        )}
        <form onSubmit={submit}>
          {mode === 'signup' && (
            <div className="field">
              <label>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </div>
          )}
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          {mode !== 'forgot' && (
            <div className="field">
              <label>Password</label>
              <input
                type="password"
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
            </div>
          )}
          {error && <p className="error">{error}</p>}
          {info && <p className="ok">{info}</p>}
          <button disabled={busy} type="submit">
            {mode === 'signup' ? 'Create account' : mode === 'login' ? 'Sign in' : 'Send reset link'}
          </button>
        </form>
        <div style={{ marginTop: 12 }}>
          <button
            className="secondary"
            onClick={() => void authClient.signIn.social({ provider: 'github', callbackURL: '/' })}
          >
            Continue with GitHub
          </button>
        </div>
        <p className="muted" style={{ marginTop: 14 }}>
          {mode === 'login' ? (
            <>
              New here?{' '}
              <Link to="/signup" state={{ from: returnTo }}>
                Create an account
              </Link>{' '}
              · <Link to="/forgot-password">Forgot password?</Link>
            </>
          ) : (
            <Link to="/login">Back to sign in</Link>
          )}
        </p>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const token = params.get('token') ?? '';
  return (
    <div className="auth">
      <div className="card">
        <h1>Choose a new password</h1>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await authClient.resetPassword({ newPassword: password, token });
            if (r.error) setError(r.error.message ?? 'Failed');
            else nav('/login');
          }}
        >
          <div className="field">
            <label>New password</label>
            <input
              type="password"
              minLength={10}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && <p className="error">{error}</p>}
          <button type="submit">Update password</button>
        </form>
      </div>
    </div>
  );
}
